import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBrand } from '../../context/BrandContext';
import { excludeCompanyLevel, isCompanyLevelBrand } from '../../utils/brandFilters';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack
} from '@mui/material';
import {
  Warehouse as WarehouseIcon,
  Inventory as InventoryIcon,
  SwapHoriz as TransferIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  AssignmentReturn as ReturnIcon,
  FactCheck as CountIcon,
  ArrowForward as ArrowForwardIcon,
  TrendingUp as TrendingUpIcon,
  Add as AddIcon,
  Business as BusinessIcon,
  Sync as SyncIcon,
  PlayArrow as PlayIcon
} from '@mui/icons-material';
import {
  getWarehouses,
  getExpiringBatches,
  getTransfers,
  getReturns,
  getDashboardSummary,
  getCompanySummary,
  getReconciliationStatus,
  runReconciliation,
  formatCurrency,
  formatNumber
} from '../../services/inventoryService';
import api from '../../config/api';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';

async function fetchInTransitSummary(brandId) {
  // Returns BOTH the aggregate totals (legacy shape) AND the per-category split
  // (added 2026-05-22 to separate genuine in-flight stock from CIN7-cutover
  // residual rows parked at warehouse_code='GIT'). Both fields are returned so
  // the dashboard can render them as two distinct lines without burying the
  // 21,022-unit migration backlog inside the "in transit" headline.
  const empty = { in_transit_qty: 0, in_transit_value: 0, sku_count: 0 };
  try {
    const params = brandId ? { brand_id: brandId } : {};
    const r = await api.get(`${API_BASE}/api/batches/in-transit/summary`, { params });
    return {
      totals: r.data?.totals || empty,
      by_category: r.data?.totals_by_category || {
        actually_in_transit: empty,
        pending_reconciliation: empty,
      },
    };
  } catch {
    return {
      totals: empty,
      by_category: { actually_in_transit: empty, pending_reconciliation: empty },
    };
  }
}

// Status colors
const STATUS_COLORS = {
  active: 'success',
  draft: 'default',
  in_transit: 'warning',
  completed: 'success',
  cancelled: 'error',
  pending: 'warning',
  received: 'info',
  processed: 'success'
};

function InventoryDashboard() {
  const navigate = useNavigate();
  const { selectedBrand, brands, selectBrand } = useBrand();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  // All Brands mode
  const [showAllBrands, setShowAllBrands] = useState(true); // Default to All Brands
  const [brandSummaries, setBrandSummaries] = useState([]); // Per-brand breakdown

  // Dashboard data
  const [warehouses, setWarehouses] = useState([]);
  // Per-warehouse rollup keyed by UPPER(warehouse code):
  // { [code]: { value, uniqueSkusWithStock } } — powers the Value + SKUs-in-stock
  // columns on the Warehouses tab. Sourced from the same summary the KPI cards use,
  // so it reconciles with the Inventory Value headline.
  const [warehouseStats, setWarehouseStats] = useState({});
  const [expiringBatches, setExpiringBatches] = useState([]);
  const [recentTransfers, setRecentTransfers] = useState([]);
  const [pendingReturns, setPendingReturns] = useState([]);

  // Summary stats
  const [stats, setStats] = useState({
    totalSkus: 0,
    totalUnits: 0,
    totalValue: 0,
    lowStockCount: 0,
    expiringCount: 0,
    expiredCount: 0,
    pendingTransfers: 0,
    pendingReturns: 0,
    externalSkus: 0,
    externalUnits: 0,
    brandCount: 0,
    inTransitQty: 0,
    inTransitValue: 0,
    inTransitSkus: 0,
    // Categorised split — see fetchInTransitSummary. _actual is genuine
    // dispatched-but-not-received stock (post producer-wiring); _pending is
    // the CIN7-cutover residual at warehouse_code='GIT' awaiting per-batch
    // reconciliation. UI renders these as separate lines so the operator
    // doesn't read the pending backlog as live in-flight inventory.
    inTransitActualQty: 0,
    inTransitActualValue: 0,
    inTransitActualSkus: 0,
    inTransitPendingQty: 0,
    inTransitPendingValue: 0,
    inTransitPendingSkus: 0,
  });

  // Reconciliation status
  const [reconciliationStatus, setReconciliationStatus] = useState({
    shipstation: { unprocessed: 0 },
    platform: { unprocessed: 0 },
    fba_transfers: { unprocessed: 0 },
    retail: { unprocessed: 0 },
    lastProcessed: null
  });
  const [reconciling, setReconciling] = useState(false);
  const [reconciliationResult, setReconciliationResult] = useState(null);
  const [cacheWarming, setCacheWarming] = useState(false);

  // If the persisted brand is the synthetic "Company Level", show All Brands instead
  // (Company Level is no longer a selectable inventory brand — All Brands consolidates).
  useEffect(() => {
    if (selectedBrand && isCompanyLevelBrand(selectedBrand) && !showAllBrands) {
      setShowAllBrands(true);
    }
  }, [selectedBrand, showAllBrands]);

  // Load data on mount - default to All Brands
  useEffect(() => {
    if (showAllBrands) {
      loadCompanyData();
    } else if (selectedBrand?.id && !isCompanyLevelBrand(selectedBrand)) {
      loadDashboardData();
    }
  }, [selectedBrand?.id, showAllBrands]);

  const handleBrandChange = (event) => {
    const brandId = event.target.value;
    if (brandId === 'all') {
      setShowAllBrands(true);
      // Don't change selectedBrand - just toggle the view
    } else {
      setShowAllBrands(false);
      const brand = brands.find(b => b.id === brandId);
      if (brand) {
        selectBrand(brand);
      }
    }
  };

  // Navigate from a summary tile into Stock Levels with the CORRECT brand scope.
  // The tiles here are COMPANY-WIDE (All Brands), but Stock Levels renders a
  // per-SKU list only for a single brand — its All Brands mode shows consolidated
  // totals with no per-SKU rows. Previously every tile navigated with just
  // `?filter=...` and no brand, so Stock Levels fell back to whatever single brand
  // was last persisted in BrandContext and silently rendered nothing when that
  // brand had none of the flagged stock (the "Expiring Soon opens an empty page"
  // bug). Route deterministically instead:
  //   - a specific brand in focus   -> that brand (+ filter): shows its SKUs
  //   - All Brands, no filter        -> consolidated All Brands view
  //   - All Brands, expiring/low     -> if exactly one brand owns the flagged
  //     SKUs, drill into it (shows them); otherwise the consolidated All Brands
  //     view (the "Inventory by Brand" table below already breaks it down).
  const goToStock = (filter) => {
    const qs = filter ? `&filter=${filter}` : '';
    if (!showAllBrands && selectedBrand?.id && !isCompanyLevelBrand(selectedBrand)) {
      navigate(`/inventory/stock?brand=${selectedBrand.id}${qs}`);
      return;
    }
    if (!filter) {
      navigate('/inventory/stock?brand=all');
      return;
    }
    const owns = (b) => filter === 'expiring'
      ? ((b.expiringCount || 0) > 0 || (b.expiredCount || 0) > 0)
      : ((b.lowStockCount || 0) > 0);
    const contributing = brandSummaries.filter(owns);
    if (contributing.length === 1) {
      navigate(`/inventory/stock?brand=${contributing[0].brandId}&filter=${filter}`);
    } else {
      navigate(`/inventory/stock?brand=all&filter=${filter}`);
    }
  };

  // Load company-wide summary (All Brands view)
  const loadCompanyData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [companySummaryRes, warehousesRes, transfersRes, inTransitTotals] = await Promise.all([
        getCompanySummary(),
        getWarehouses({ is_active: true }),
        getTransfers({ status: 'in_transit', limit: 10 }),
        fetchInTransitSummary(null),
      ]);

      const summaryData = companySummaryRes?.data || {};
      const warehouseList = warehousesRes?.warehouses ?? warehousesRes?.data?.warehouses ?? [];
      const transferList = transfersRes?.transfers || [];
      const transferTotal = transfersRes?.total || 0;

      setWarehouses(warehouseList);
      setWarehouseStats(summaryData.warehouses || {});
      setRecentTransfers(transferList);
      setExpiringBatches([]); // No expiring batches in all-brands view
      setPendingReturns([]); // No returns in all-brands view

      // Set stats from company-wide totals. inTransitTotals is now the full
      // response from fetchInTransitSummary — { totals, by_category }.
      const itActual = inTransitTotals.by_category?.actually_in_transit || {};
      const itPending = inTransitTotals.by_category?.pending_reconciliation || {};
      setStats({
        totalSkus: summaryData.totals?.totalSkus || 0,
        totalUnits: summaryData.totals?.totalUnits || 0,
        totalValue: summaryData.totals?.totalValue || 0,
        lowStockCount: summaryData.totals?.lowStockCount || 0,
        expiringCount: summaryData.totals?.expiringCount || 0,
        expiredCount: summaryData.totals?.expiredCount || 0,
        pendingTransfers: transferTotal,
        pendingReturns: 0,
        externalSkus: summaryData.totals?.externalSkus || 0,
        externalUnits: summaryData.totals?.externalUnits || 0,
        brandCount: summaryData.totals?.brandCount || 0,
        inTransitQty: Number(inTransitTotals.totals?.in_transit_qty || 0),
        inTransitValue: Number(inTransitTotals.totals?.in_transit_value || 0),
        inTransitSkus: Number(inTransitTotals.totals?.sku_count || 0),
        inTransitActualQty: Number(itActual.in_transit_qty || 0),
        inTransitActualValue: Number(itActual.in_transit_value || 0),
        inTransitActualSkus: Number(itActual.sku_count || 0),
        inTransitPendingQty: Number(itPending.in_transit_qty || 0),
        inTransitPendingValue: Number(itPending.in_transit_value || 0),
        inTransitPendingSkus: Number(itPending.sku_count || 0),
      });

      // Store per-brand breakdown for display
      setBrandSummaries(summaryData.brands || []);

      // Show warming indicator if cache is cold
      if (summaryData.isWarm === false || summaryData.isPartial) {
        setCacheWarming(true);
        // Auto-refresh after 60 seconds to pick up warmed data
        setTimeout(() => {
          setCacheWarming(false);
          loadCompanyData();
        }, 60000);
      } else {
        setCacheWarming(false);
      }

    } catch (err) {
      console.error('Failed to load company data:', err);
      setError('Failed to load company inventory data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadDashboardData = async () => {
    if (!selectedBrand?.id) {
      setError('Please select a brand to view inventory data');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Load all data in parallel
      const [
        warehousesRes,
        summaryRes,
        expiringRes,
        transfersRes,
        returnsRes,
        reconciliationRes
      ] = await Promise.all([
        getWarehouses({ is_active: true, brand_id: selectedBrand.id }),
        getDashboardSummary(selectedBrand.id),
        getExpiringBatches({ brandId: selectedBrand.id, days: 90 }),  // "Expiring Soon" = within 3 months
        getTransfers({ status: 'in_transit', limit: 10 }),  // Transfers are company-wide, not brand-specific
        getReturns({ brandId: selectedBrand.id, status: 'pending', limit: 10 }),
        getReconciliationStatus({ brand_id: selectedBrand.id }).catch(() => ({ status: {} }))
      ]);
      const inTransitTotals = await fetchInTransitSummary(selectedBrand.id);

      const warehouseList = warehousesRes?.warehouses ?? warehousesRes?.data?.warehouses ?? [];
      const summaryData = summaryRes?.data || {};
      const expiringList = expiringRes?.data?.batches || [];
      const transferList = transfersRes?.transfers || [];
      const transferTotal = transfersRes?.total || 0;
      const returnList = returnsRes?.data || [];

      // Set reconciliation status
      if (reconciliationRes?.status) {
        setReconciliationStatus(reconciliationRes.status);
      }

      setWarehouses(warehouseList);
      setWarehouseStats(summaryData.warehouses || {});
      setExpiringBatches(expiringList);
      setRecentTransfers(transferList);
      setPendingReturns(returnList);

      // Use summary data from dedicated API (movement-based calculations)
      const itActualB = inTransitTotals.by_category?.actually_in_transit || {};
      const itPendingB = inTransitTotals.by_category?.pending_reconciliation || {};
      setStats({
        totalSkus: summaryData.totalSkus || 0,
        totalUnits: summaryData.totalUnits || 0,
        totalValue: summaryData.totalValue || 0,
        lowStockCount: summaryData.lowStockCount || 0,
        expiringCount: summaryData.expiringCount || expiringList.length,
        expiredCount: summaryData.expiredCount || 0,
        pendingTransfers: transferTotal,
        pendingReturns: returnList.length,
        // External stock (Amazon FBA)
        externalSkus: summaryData.externalSkus || 0,
        externalUnits: summaryData.externalUnits || 0,
        // In-transit (state-based, separate from physical on-hand). Aggregate
        // + per-category split (post 2026-05-22 refactor to separate genuine
        // in-flight from CIN7-cutover residuals).
        inTransitQty: Number(inTransitTotals.totals?.in_transit_qty || 0),
        inTransitValue: Number(inTransitTotals.totals?.in_transit_value || 0),
        inTransitSkus: Number(inTransitTotals.totals?.sku_count || 0),
        inTransitActualQty: Number(itActualB.in_transit_qty || 0),
        inTransitActualValue: Number(itActualB.in_transit_value || 0),
        inTransitActualSkus: Number(itActualB.sku_count || 0),
        inTransitPendingQty: Number(itPendingB.in_transit_qty || 0),
        inTransitPendingValue: Number(itPendingB.in_transit_value || 0),
        inTransitPendingSkus: Number(itPendingB.sku_count || 0),
      });

    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError('Failed to load inventory data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle running batch reconciliation
  const handleRunReconciliation = async () => {
    if (!selectedBrand?.id) return;

    setReconciling(true);
    setReconciliationResult(null);

    try {
      const result = await runReconciliation({ brand_id: selectedBrand.id, limit: 1000 });
      setReconciliationResult(result.results);

      // Refresh the status after running
      const statusRes = await getReconciliationStatus({ brand_id: selectedBrand.id });
      if (statusRes?.status) {
        setReconciliationStatus(statusRes.status);
      }
    } catch (err) {
      console.error('Reconciliation failed:', err);
      setError('Reconciliation failed. Please try again.');
    } finally {
      setReconciling(false);
    }
  };

  // Calculate total pending reconciliation
  const totalPendingReconciliation =
    (reconciliationStatus?.shipstation?.unprocessed || 0) +
    (reconciliationStatus?.platform?.unprocessed || 0) +
    (reconciliationStatus?.fba_transfers?.unprocessed || 0) +
    (reconciliationStatus?.retail?.unprocessed || 0);

  const StatCard = ({ title, value, subtitle, icon, color = 'primary', onClick }) => (
    <Card 
      sx={{ 
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': onClick ? {
          transform: 'translateY(-2px)',
          boxShadow: 4
        } : {}
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography color="textSecondary" variant="body2" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" component="div" fontWeight="bold">
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              backgroundColor: `${color}.light`,
              borderRadius: 2,
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {React.cloneElement(icon, { sx: { color: `${color}.main`, fontSize: 28 } })}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const QuickActionButton = ({ icon, label, onClick, color = 'primary' }) => (
    <Button
      variant="outlined"
      startIcon={icon}
      onClick={onClick}
      sx={{
        borderRadius: 2,
        py: 1.5,
        px: 2,
        textTransform: 'none',
        justifyContent: 'flex-start',
        borderColor: `${color}.main`,
        color: `${color}.main`,
        '&:hover': {
          backgroundColor: `${color}.light`,
          borderColor: `${color}.main`
        }
      }}
      fullWidth
    >
      {label}
    </Button>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Brand Selector Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={3} alignItems="center">
            <BusinessIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight={600} gutterBottom>
                Inventory Management
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Overview of stock levels, movements, and operations
              </Typography>
            </Box>
            <FormControl sx={{ minWidth: 250 }}>
              <InputLabel>Select Brand</InputLabel>
              <Select
                value={(showAllBrands || isCompanyLevelBrand(selectedBrand)) ? 'all' : (selectedBrand?.id || '')}
                onChange={handleBrandChange}
                label="Select Brand"
              >
                <MenuItem value="all">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessIcon fontSize="small" />
                    <strong>All Brands</strong>
                  </Box>
                </MenuItem>
                {excludeCompanyLevel(brands).map(brand => (
                  <MenuItem key={brand.id} value={brand.id}>
                    {brand.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title="Refresh data">
              <IconButton onClick={showAllBrands ? loadCompanyData : loadDashboardData} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </CardContent>
      </Card>

      {/* Brand Selection Alert - only show when not in All Brands mode and no brand selected */}
      {!showAllBrands && !selectedBrand && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Please select a brand above to view inventory data
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {cacheWarming && showAllBrands && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Inventory data is being computed across all brands. This page will auto-refresh shortly.
        </Alert>
      )}

      {/* Summary Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total SKUs"
            value={formatNumber(stats.totalSkus)}
            // Total SKUs/Units count FINISHED GOODS only (L1 or prepacked) while Inventory Value
            // below spans every level including components — a deliberate split (24524ff3c) that
            // was previously invisible, so Value / Units did not divide into a sensible unit cost.
            subtitle={`${formatNumber(stats.totalUnits)} units · finished goods only`}
            icon={<InventoryIcon />}
            color="primary"
            onClick={() => goToStock()}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Inventory Value"
            value={formatCurrency(stats.totalValue)}
            subtitle={showAllBrands
              ? `Across ${stats.brandCount} brands · incl. components`
              : `Across ${warehouses.length} warehouses · incl. components`}
            icon={<TrendingUpIcon />}
            color="success"
            onClick={showAllBrands ? undefined : () => navigate('/inventory/stock')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Low Stock Items"
            value={formatNumber(stats.lowStockCount)}
            // NOT a reorder point. stock-levels-service.js getInventoryDashboardSummary counts a
            // SKU as low when qty > 0 AND (qty < 10 OR (90-day sales > 0 AND qty < 90-day sales)).
            // A real reorder-point engine exists (lead-time-engine/compute.js) and is not consulted
            // here, so the label described a calculation the screen does not perform.
            subtitle="Under 10 units, or below 90-day demand"
            icon={<WarningIcon />}
            color={stats.lowStockCount > 0 ? 'warning' : 'success'}
            onClick={() => goToStock('low')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={stats.expiredCount > 0 ? "Expiry Alert" : "Expiring Soon"}
            value={stats.expiredCount > 0 ? formatNumber(stats.expiredCount) : formatNumber(stats.expiringCount)}
            subtitle={stats.expiredCount > 0
              ? `${formatNumber(stats.expiredCount)} expired, ${formatNumber(stats.expiringCount)} expiring (3mo)`
              : "Within 3 months"}
            icon={<WarningIcon />}
            color={(stats.expiredCount > 0 || stats.expiringCount > 0) ? 'error' : 'success'}
            onClick={() => goToStock('expiring')}
          />
        </Grid>
      </Grid>

      {/* In-Transit callout — split into TWO lines (2026-05-22) so the
          CIN7-cutover migration residual ("pending reconciliation") doesn't
          get conflated with genuine dispatched-but-not-received stock. The
          actually_in_transit line will grow once producer wiring goes live;
          today it shows 0 because no dispatch handler writes the state yet. */}
      {(stats.inTransitActualQty > 0 || stats.inTransitPendingQty > 0) && (
        <Card sx={{ mb: 4, borderLeft: '4px solid', borderColor: 'info.main' }}>
          <CardContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {/* Line 1 — actual in-flight stock (clickable → in-transit transfer list) */}
              <Box
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  flexWrap: 'wrap', gap: 2,
                  cursor: stats.inTransitActualQty > 0 ? 'pointer' : 'default',
                  borderRadius: 1, mx: -1, px: 1, py: 0.5,
                  '&:hover': stats.inTransitActualQty > 0 ? { bgcolor: 'action.hover' } : {},
                }}
                onClick={() => stats.inTransitActualQty > 0 && navigate('/inventory/transfers?status=in_transit')}
                title={stats.inTransitActualQty > 0 ? 'Open the in-transit transfer list' : ''}
              >
                <Box>
                  <Typography variant="overline" color="textSecondary">In transit — dispatched, not yet at destination</Typography>
                  <Typography variant="h6">
                    {formatNumber(stats.inTransitActualQty)} units · {formatCurrency(stats.inTransitActualValue)}
                    <Typography component="span" variant="body2" color="textSecondary" sx={{ ml: 1 }}>
                      across {formatNumber(stats.inTransitActualSkus)} SKUs
                    </Typography>
                    {stats.inTransitActualQty > 0 && (
                      <Typography component="span" variant="body2" color="primary.main" sx={{ ml: 1, fontWeight: 500 }}>
                        · view list →
                      </Typography>
                    )}
                  </Typography>
                </Box>
                <Typography variant="caption" color="textSecondary" sx={{ maxWidth: 360 }}>
                  Batches at their real destination warehouse with state=in_transit.
                  Excluded from on-hand totals above until physically received.
                </Typography>
              </Box>
              {/* Line 2 — CIN7 migration residual */}
              {stats.inTransitPendingQty > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, pt: 1.5, borderTop: '1px dashed', borderColor: 'divider' }}>
                  <Box>
                    <Typography variant="overline" color="warning.dark">Pending CIN7 reconciliation</Typography>
                    <Typography variant="h6">
                      {formatNumber(stats.inTransitPendingQty)} units · {formatCurrency(stats.inTransitPendingValue)}
                      <Typography component="span" variant="body2" color="textSecondary" sx={{ ml: 1 }}>
                        across {formatNumber(stats.inTransitPendingSkus)} SKUs
                      </Typography>
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="warning.dark" sx={{ maxWidth: 360 }}>
                    Migration residue from the 2026-05-19 CIN7 cutover — parked at virtual
                    GIT warehouse pending per-batch reconciliation against incoming POs.
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Brand Breakdown Table - Only shown when All Brands is selected */}
      {showAllBrands && brandSummaries.length > 0 && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Inventory by Brand
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Brand</TableCell>
                    <TableCell align="right">SKUs</TableCell>
                    <TableCell align="right">Units</TableCell>
                    <TableCell align="right">Value</TableCell>
                    <TableCell align="right">Low Stock</TableCell>
                    <TableCell align="right">Expiring</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {brandSummaries.map((brand) => (
                    <TableRow key={brand.brandId} hover>
                      <TableCell>
                        {/* Clicking the brand name opens Stock Levels scoped to that
                            brand. StockLevels reads ?brand=<id> from the URL and calls
                            selectBrand (StockLevels.js) — same deterministic pattern as
                            goToStock above, so no need to touch BrandContext here. */}
                        <Typography
                          fontWeight="medium"
                          onClick={() => navigate(`/inventory/stock?brand=${brand.brandId}`)}
                          sx={{
                            color: 'primary.main',
                            cursor: 'pointer',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          {brand.brandName}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{formatNumber(brand.totalSkus)}</TableCell>
                      <TableCell align="right">{formatNumber(brand.totalUnits)}</TableCell>
                      <TableCell align="right">{formatCurrency(brand.totalValue)}</TableCell>
                      <TableCell align="right">
                        {brand.lowStockCount > 0 ? (
                          <Chip label={brand.lowStockCount} size="small" color="warning" />
                        ) : (
                          <Typography color="text.secondary">0</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {(brand.expiredCount || 0) > 0 ? (
                          <Chip label={`${brand.expiredCount} expired`} size="small" color="error" sx={{ mr: 0.5 }} />
                        ) : null}
                        {brand.expiringCount > 0 ? (
                          <Chip label={brand.expiringCount} size="small" color="warning" />
                        ) : (brand.expiredCount || 0) === 0 ? (
                          <Typography color="text.secondary">0</Typography>
                        ) : null}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          onClick={() => {
                            const brandObj = brands.find(b => b.id === brand.brandId);
                            if (brandObj) {
                              setShowAllBrands(false);
                              selectBrand(brandObj);
                            }
                          }}
                        >
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Batch Reconciliation Status */}
      {totalPendingReconciliation > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={reconciling ? <CircularProgress size={16} color="inherit" /> : <PlayIcon />}
              onClick={handleRunReconciliation}
              disabled={reconciling}
            >
              {reconciling ? 'Processing...' : 'Run Reconciliation'}
            </Button>
          }
        >
          <Typography variant="subtitle2" fontWeight="bold">
            {formatNumber(totalPendingReconciliation)} shipments pending batch reconciliation
          </Typography>
          <Typography variant="body2">
            ShipStation: {formatNumber(reconciliationStatus?.shipstation?.unprocessed || 0)} |
            Platform: {formatNumber(reconciliationStatus?.platform?.unprocessed || 0)} |
            FBA: {formatNumber(reconciliationStatus?.fba_transfers?.unprocessed || 0)} |
            Retail: {formatNumber(reconciliationStatus?.retail?.unprocessed || 0)}
          </Typography>
        </Alert>
      )}

      {/* Reconciliation Result */}
      {reconciliationResult && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          onClose={() => setReconciliationResult(null)}
        >
          <Typography variant="subtitle2" fontWeight="bold">
            Reconciliation Complete
          </Typography>
          <Typography variant="body2">
            Processed: ShipStation {reconciliationResult.shipstation?.processed || 0} |
            Platform {reconciliationResult.platform?.processed || 0} |
            FBA {reconciliationResult.fba_transfers?.processed || 0} |
            Retail {reconciliationResult.retail?.processed || 0}
          </Typography>
        </Alert>
      )}

      {/* Quick Actions */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Quick Actions
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <QuickActionButton
                icon={<TransferIcon />}
                label="New Transfer"
                onClick={() => navigate('/inventory/transfers/new')}
                color="primary"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <QuickActionButton
                icon={<ReturnIcon />}
                label="Process Return"
                onClick={() => navigate('/inventory/returns')}
                color="secondary"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <QuickActionButton
                icon={<CountIcon />}
                label="Physical Count"
                onClick={() => navigate('/inventory/counts')}
                color="info"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <QuickActionButton
                icon={<AddIcon />}
                label="Add Products"
                onClick={() => navigate('/inventory/cogs')}
                color="success"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Main Content Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
            <Tab label={`Warehouses (${warehouses.length})`} />
            <Tab label={`In Transit (${stats.pendingTransfers})`} />
            <Tab label={`Pending Returns (${stats.pendingReturns})`} />
            {expiringBatches.length > 0 && <Tab label={`Expiring (${stats.expiringCount})`} />}
          </Tabs>
        </Box>
        
        <CardContent>
          {/* Warehouses Tab */}
          {activeTab === 0 && (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Warehouse</TableCell>
                    <TableCell>Code</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell align="right">Inventory Value</TableCell>
                    <Tooltip title="Unique SKUs with stock greater than 0 at this warehouse">
                      <TableCell align="right">SKUs in Stock</TableCell>
                    </Tooltip>
                    <TableCell align="right">Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {warehouses.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography color="textSecondary" sx={{ py: 3 }}>
                          {showAllBrands ? (
                            <>No warehouses configured. <Button onClick={() => navigate('/inventory/warehouses')}>Add Warehouse</Button></>
                          ) : (
                            <>No warehouses hold inventory for {selectedBrand?.name || 'this brand'}.</>
                          )}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    warehouses.map((warehouse) => {
                      // Rollup is keyed by UPPER(code); a warehouse with no valued
                      // stock in the movement engine simply has no entry → 0 / $0.
                      const whStat = warehouseStats[String(warehouse.code || '').trim().toUpperCase()] || {};
                      return (
                      <TableRow key={warehouse.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <WarehouseIcon color="action" />
                            <Typography fontWeight="medium">{warehouse.name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={warehouse.code} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          {[warehouse.city, warehouse.country].filter(Boolean).join(', ') || '—'}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(whStat.value || 0)}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(whStat.uniqueSkusWithStock || 0)}
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={warehouse.is_active ? 'Active' : 'Inactive'}
                            size="small"
                            color={warehouse.is_active ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            endIcon={<ArrowForwardIcon />}
                            onClick={() => navigate(`/inventory/warehouses/${warehouse.id}`)}
                          >
                            View Stock
                          </Button>
                        </TableCell>
                      </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* In Transit Tab */}
          {activeTab === 1 && (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Transfer #</TableCell>
                    <TableCell>Route</TableCell>
                    <TableCell>Items</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentTransfers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography color="textSecondary" sx={{ py: 3 }}>
                          No transfers in transit
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    recentTransfers.map((transfer) => (
                      <TableRow key={transfer.id} hover>
                        <TableCell>
                          <Typography fontWeight="medium">{transfer.transfer_number}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Chip label={transfer.from_warehouse_code} size="small" />
                            <ArrowForwardIcon fontSize="small" color="action" />
                            <Chip label={transfer.to_warehouse_code} size="small" />
                          </Box>
                        </TableCell>
                        <TableCell>{transfer.total_items || 0} {(transfer.total_items || 0) === 1 ? 'item' : 'items'} · {formatNumber(transfer.total_quantity || 0)} {(transfer.total_quantity || 0) === 1 ? 'unit' : 'units'}</TableCell>
                        <TableCell>
                          <Chip
                            label={transfer.status}
                            size="small"
                            color={STATUS_COLORS[transfer.status] || 'default'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            onClick={() => navigate(`/inventory/transfers/${transfer.id}`)}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              {recentTransfers.length > 0 && (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Button onClick={() => navigate('/inventory/transfers')}>
                    View All Transfers
                  </Button>
                </Box>
              )}
            </TableContainer>
          )}

          {/* Pending Returns Tab */}
          {activeTab === 2 && (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Return #</TableCell>
                    <TableCell>Order</TableCell>
                    <TableCell>Warehouse</TableCell>
                    <TableCell>Condition</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingReturns.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography color="textSecondary" sx={{ py: 3 }}>
                          No pending returns
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    pendingReturns.map((ret) => (
                      <TableRow key={ret.id} hover>
                        <TableCell>
                          <Typography fontWeight="medium">{ret.return_id}</Typography>
                        </TableCell>
                        <TableCell>{ret.order_id}</TableCell>
                        <TableCell>
                          <Chip label={ret.warehouse_code} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={ret.condition || 'Unknown'}
                            size="small"
                            color={ret.condition === 'sellable' ? 'success' : 'warning'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={ret.status}
                            size="small"
                            color={STATUS_COLORS[ret.status] || 'default'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => navigate(`/inventory/returns/${ret.id}`)}
                          >
                            Process
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              {pendingReturns.length > 0 && (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Button onClick={() => navigate('/inventory/returns')}>
                    View All Returns
                  </Button>
                </Box>
              )}
            </TableContainer>
          )}

          {/* Expiring Batches Tab */}
          {activeTab === 3 && expiringBatches.length > 0 && (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>SKU</TableCell>
                    <TableCell>Batch</TableCell>
                    <TableCell>Warehouse</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell>Expiry Date</TableCell>
                    <TableCell>Days Left</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {expiringBatches.map((batch) => (
                    <TableRow key={batch.id} hover>
                      <TableCell>
                        <Typography fontWeight="medium">{batch.sku}</Typography>
                      </TableCell>
                      <TableCell>{batch.batch_number}</TableCell>
                      <TableCell>
                        <Chip label={batch.warehouse_code} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="right">{formatNumber(batch.current_quantity)}</TableCell>
                      <TableCell>{batch.expiry_date}</TableCell>
                      <TableCell>
                        <Chip
                          label={`${batch.days_until_expiry} days`}
                          size="small"
                          color={batch.days_until_expiry <= 7 ? 'error' : batch.days_until_expiry <= 14 ? 'warning' : 'default'}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default InventoryDashboard;
